import { useEffect, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { btnSecondary } from '../ui/styles';
import { useAdmin } from '../AdminContext';
import { useSettings } from './useSettings';
import { StaffRow } from './StaffRow';
import { OtherLogins } from './OtherLogins';
import { fetchStaffLogins } from '../adminApi';
import { isAbortError } from '../../core/api/http';
import { initialsOf, staffList } from '../../core/rules/salonDays';
import { clashingColors, nextStaffColor } from '../../core/rules/staffColors';
import type { StaffProfile } from '../../core/types/site';
import type { StaffLogin } from '../../core/types/adminRequests';
type Props = {
  onOpenProfile: () => void;
};

/**
 * Instellingen › Team (docs/KASSA-ABONNEMENTEN.md §4). Het profiel (naam, behandelingen, gestopt)
 * gaat mee met "Opslaan"; een login uitnodigen of intrekken gebeurt meteen, want daar krijgt iemand
 * een mail van. Sarahs eigen profiel staat op één plek: Salon & contact › Over jou.
 */
export function StaffSettings({
  onOpenProfile
}: Props) {
  const {
    site,
    setSite
  } = useSettings();
  const {
    catalog
  } = useAdmin();
  const team = staffList(site.staff);
  const [logins, setLogins] = useState<StaffLogin[] | null>(null);
  const [loginError, setLoginError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    fetchStaffLogins({
      signal: controller.signal
    }).then(setLogins).catch((err: unknown) => {
      if (!isAbortError(err)) setLoginError(true);
    });
    return () => controller.abort();
  }, []);
  const setLogin = (staffId: string, login: StaffLogin | null) => setLogins(list => [...(list ?? []).filter(l => l.staffId !== staffId), ...(login ? [login] : [])]);
  const setTeam = (next: StaffProfile[]) => setSite(s => ({
    ...s,
    staff: next
  }));
  const update = (id: string, patch: Partial<StaffProfile>) => setTeam(team.map(s => s.id === id ? {
    ...s,
    ...patch
  } : s));
  const add = () => {
    const name = 'Nieuwe collega';
    const id = `collega-${Math.random().toString(36).slice(2, 7)}`;
    const color = nextStaffColor(team);
    setTeam([...team, {
      id,
      name,
      initials: initialsOf(name),
      color,
      role: 'staff',
      allTreatments: false,
      treatments: [],
      active: true
    }]);
  };
  const salonTreatments = catalog.treatments.filter(t => !t.online);
  const clashes = clashingColors(team);
  return <>
      <Panel title="Medewerkers" description="Wie er in de salon behandelt. Een collega ziet in het beheer alleen haar eigen afspraken, rondt ze af en rekent ze af. Ze ziet geen omzet, bestellingen of instellingen." actions={<button type="button" onClick={add} className={btnSecondary} data-id="element-4244" data-mp="2dgky487pn4qj">
            <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4245" data-mp="28k40mnemfe0s" />
            Collega
          </button>} data-id="element-4243" data-mp="24h7ptivbom7e">
        {clashes.length > 0 && <p role="status" className="mb-3 rounded-md bg-blush/60 px-3 py-2.5 text-[13px] text-ink" data-id="element-4246" data-mp="e6cva03brgb5">
            {clashes.map(c => team.filter(s => s.active && s.color === c).map(s => s.name).join(' en ')).join('; ')} hebben dezelfde kleur. Kies voor
            ieder een eigen kleur, anders zijn hun afspraken in de agenda niet uit elkaar te houden.
          </p>}
        <p className="mb-4 rounded-md bg-shell px-3 py-2.5 text-[13px] text-muted" data-id="element-4247" data-mp="2l1ydcoxoecii">
          Uitnodigen en een login intrekken gebeuren meteen, los van Opslaan: je collega krijgt er direct een mail van. De rest bewaar je met Opslaan.
        </p>
        <ul className="divide-y divide-line" data-id="element-4248" data-mp="2c336damt4ye5">
          {team.map(s => <StaffRow key={s.id} profile={s} treatments={salonTreatments} login={logins?.find(l => l.staffId === s.id) ?? null} loginsUnavailable={loginError} onChange={patch => update(s.id, patch)} onOpenProfile={onOpenProfile} onLogin={login => setLogin(s.id, login)} data-id="element-4249" data-mp="1y2sdy35unbqv" />)}
        </ul>
      </Panel>
      <OtherLogins logins={(logins ?? []).filter(l => !team.some(s => s.id === l.staffId))} onLogin={setLogin} data-id="element-4250" data-mp="37ompxqbmgmje" />
    </>;
}